import { Helmet } from 'react-helmet';
import { useMemo, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePostMessage, useSubscribeData } from 'spectacle-ui-game-bridge-react';
import { getPath } from '../../config';
import { usePreloginContext } from '../../context/PreloginContext';
import Debug from '../../components/debug/debug';
import ErrorModal from '../../components/errorModal/errorModal';
import invlerp from '../../utils/invlerp';
import LoadingIndicator from '../../components/loadingIndicator/loadingIndicator';
import NotificationsPrompt from '../../components/notificationsPrompt/notificationsPrompt';
import Odometer from '../../components/odometer/odometer';
import useBodyClass from '../../hooks/useBodyClass';
import useCDNI from '../../hooks/useCDNI';
import useContentForBuild from '../../hooks/useContentForBuild';
import useDebouncedDlogFunnel from '../../hooks/useDebouncedDlogFunnel';
import useDlogLoginFunnel, { LoginFunnelAction } from '../../hooks/useDlogLoginFunnel';
import VideoPlayer from '../../components/videoPlayer/videoPlayer';
import './cdni.sass';

/*
CDNIStateEnum Values
0( InitialState )
1( ExtractOldShard )
2( CacheValidation )
3( CheckNetworkConnection )
4( CheckDevNetwork )
5( WaitServerMetaData )
6( RequestManifest )
7( FetchingManifest )
8( ManifestError )
9( ManifestOk )
10( PrepareDownload )
11( CheckDiskSpaceFailed )
12( StartDownload )
13( QosTest )
14( QosRangeTest )
15( CellularDownloadChecking )
16( Downloading )
17( DownloadComplete )
18( WaitGarbageCollection )
19( WaitUserInput )
20( ExitInstallStage )
21( EnterFrontEnd )
22( Idle )
23( WaitDevVerifyCache )
24( CellularOptionalDownloadChecking )
25( ProcessOptionalDownload )
26( OptionalDownloading )
27( OptionalDownloadComplete )
28( OptionalDownloadError )
*/

export default function Cdni() {
  const dlog = useDlogLoginFunnel();
  const dlogVideoPlayed = useDebouncedDlogFunnel({
    phase: 'Video_Player_Active',
    action: 'Video_Started',
    targets: 'CDNI',
    resume: false,
  });
  const dlogVideoLooped = useDebouncedDlogFunnel({
    phase: 'Video_Player_Active',
    action: 'Video_Started',
    targets: 'CDNI',
    resume: true,
  });
  const dlogVideoStuttering = useDebouncedDlogFunnel({
    phase: 'Video_Player_Active',
    action: 'Video_Stuttering',
    targets: 'CDNI',
  });
  const [content] = useContentForBuild();
  const [currentPhase, setCurrentPhase] = useState<number>(1);
  const [postDownload, setPostDownload] = useState<boolean>(false);
  const [enteredDownloadPhase, setEnteredDownloadPhase] = useState<boolean>(false);
  const navigate = useNavigate();
  const passPreloginFence = usePostMessage('passPreloginFence');
  const hasValidToken = useSubscribeData<boolean>('tos', 'hasValidToken') ?? false; // has logged in
  const { isSoftPromptShown, notificationDecision, CDNIStateString, CDNIStateEnum } = usePreloginContext();
  const { autoLoginStatus, buffer, closeWebView, diskFreeSize, error, GetAverageDownloadSpeed, GetDownloadedSizeInBytes, getMb, GetTotalSizeToDownload, InstallingVersion, isCDNIFinished, isCheckingForUpdates, isLoading, isMobileSharedAccountEnabled, NowVersion, ShowUsingDevTip, CDNICellularDownloadTipClose, CDNIUnexpectedError, CDNIPromptName, CDNIRecoverableErrorMessage, CDNIGoToAppStore } = useCDNI(content);
  const isDownloadingFromCDNI = (!isLoading && !isCheckingForUpdates) || CDNIStateEnum === 16;
  useBodyClass('cdni');

  // Determine the size per fill as total number of phases
  let sizePerFill = 0;

  if (content) {
    sizePerFill = GetTotalSizeToDownload / content.cdniNumberOfFills;

    if (sizePerFill < content.cdniMinimumBytesPerFill) {
      const steps = Math.ceil(GetTotalSizeToDownload / content.cdniMinimumBytesPerFill);
      sizePerFill = GetTotalSizeToDownload / steps;
    }
  }

  const totalPhases = sizePerFill === 0 ? 1 : GetTotalSizeToDownload / sizePerFill;

  // Set the Current Download Phase so we can loop the progress bar
  const phaseTimoutActiveRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const shouldStartNextPhase = buffer > sizePerFill * currentPhase;
  const done = totalPhases === currentPhase;

  useEffect(() => {
    if (isDownloadingFromCDNI) {
      setEnteredDownloadPhase(true);

      dlog({
        phase: 'CDNI_Screen_Start',
        action: 'Render',
      });
    }
  }, [isDownloadingFromCDNI, dlog]);

  useEffect(() => {
    if (sizePerFill && shouldStartNextPhase && !done && !phaseTimoutActiveRef.current) {
      phaseTimoutActiveRef.current = setTimeout(() => {
        setCurrentPhase((phase) => Math.min(phase + 1, totalPhases));
        phaseTimoutActiveRef.current = null;
      }, 1000);

      return () => {
        if (phaseTimoutActiveRef.current) {
          clearTimeout(phaseTimoutActiveRef.current);
        }
      };
    }
  }, [totalPhases, shouldStartNextPhase, done, sizePerFill]);

  useEffect(() => {
    const progress = () => {
      passPreloginFence();

      if (content?.enablePostLoginMessages) {
        navigate('/post-login');
      } else {
        closeWebView();
      }
    };

    // When the download is complete and the soft prompt is not visible, navigate to the loading screen
    if ((isCDNIFinished || CDNIStateEnum === 17) && !isSoftPromptShown) {
      setPostDownload(true);
      if (isCDNIFinished) {
        if (enteredDownloadPhase) {
          dlog({
            phase: 'CDNI_Screen_Finish',
            action: 'Render',
          });
        }

        //Boolean(isUsingCachedLoginToken && isCDNIFinished)
        if (!isLoading && isMobileSharedAccountEnabled) {
          navigate('/upsell');
        } else if (isLoading) {
          if (autoLoginStatus !== null) {
            if (autoLoginStatus !== 1) {
              // 1 is a success and all other are failure scenarios
              navigate('/login');
            } else if (autoLoginStatus === 1 && hasValidToken) {
              progress();
            }
          } else if (isLoading && hasValidToken) {
            progress();
          }
        } else {
          navigate('/login');
        }
      }
    }
  }, [autoLoginStatus, isCDNIFinished, isMobileSharedAccountEnabled, isLoading, navigate, dlog, closeWebView, hasValidToken, passPreloginFence, content, isSoftPromptShown, CDNIStateEnum, enteredDownloadPhase]);

  const percentageCompleted = useMemo<number>(() => {
    let downloadedRatio: number = 0;

    if (GetAverageDownloadSpeed && buffer !== 0) {
      const start = (currentPhase - 1) * sizePerFill;
      const end = sizePerFill * currentPhase;

      downloadedRatio = invlerp(start, end, buffer);
    }

    return downloadedRatio * 100;
  }, [buffer, GetAverageDownloadSpeed, currentPhase, sizePerFill]);

  const nowVersion = useMemo<string>(() => {
    if (InstallingVersion && InstallingVersion > 0 && NowVersion !== InstallingVersion) {
      return NowVersion + ' > ' + InstallingVersion;
    } else {
      return NowVersion.toString();
    }
  }, [InstallingVersion, NowVersion]);

  const flavorText = useMemo<string>(() => {
    if (content) {
      let flavor = content.cdniProgress[Math.ceil(percentageCompleted / 10) * 10];

      if (percentageCompleted > 91) {
        flavor = content.cdniProgress['95'];

        if (percentageCompleted < 95) {
          flavor = content.cdniProgress['97'];
        } else if (percentageCompleted > 97) {
          if (currentPhase !== totalPhases) {
            flavor = content.cdniProgress['98'];
          } else {
            flavor = content.cdniProgress['98'];

            if (percentageCompleted > 98) {
              flavor = content.cdniProgress['100'];
            }

            if (percentageCompleted === 99) {
              let remainingTime = 0;

              if (!GetAverageDownloadSpeed || !GetTotalSizeToDownload || !buffer) {
                remainingTime = 0;
              } else {
                const time = GetAverageDownloadSpeed / 60;

                if (GetAverageDownloadSpeed > 0) {
                  remainingTime = Math.ceil((GetTotalSizeToDownload - buffer) / time);
                }
              }

              flavor = content.under1min.replace('1', remainingTime.toString());
            }
          }
        }
      }

      return flavor;
    }

    return '';
  }, [percentageCompleted, content, GetTotalSizeToDownload, GetAverageDownloadSpeed, buffer, currentPhase, totalPhases]);

  useEffect(() => {
    if (notificationDecision !== null) {
      dlog({
        phase: 'Notification_Request_Dialog',
        action: 'Click',
        targets: notificationDecision ? 'Allow' : 'Dont_Allow',
      });
    }
  }, [notificationDecision, dlog]);

  return (
    <>
      <Helmet>
        <title>CDNI</title>
      </Helmet>
      {postDownload ? (
        <div className="cdni__checking">
          <LoadingIndicator />
          <h1>{content?.loading}</h1>
        </div>
      ) : isDownloadingFromCDNI ? (
        <>
          <img src={getPath('wzm_logo')} alt="Call of Duty: Warzone Mobile" className="logo" />
          <div className="cdni__progress">
            <div className="cdni__state-container">
              <div className="cdni__downloading">{content?.cdniPhaseMessages[currentPhase] ?? content?.cdniPhaseMessages[1]}</div>
              <div className="cdni__state-percentage">
                <Odometer value={Math.floor(percentageCompleted)} digits={2} />%
              </div>
            </div>
            <progress
              max="100"
              value={percentageCompleted}
              className="cdni__progress-bar"
              key={currentPhase} // Note: This key resets the animation state of this element when phase updates
            >
              {percentageCompleted}%
            </progress>
            <div className="cdni__state-container">
              <div className="cdni__state-total">
                {getMb(buffer)}MB <span>/ {getMb(GetTotalSizeToDownload)}MB</span>
              </div>
              <div className="cdni__state-flavor">{flavorText}</div>
              <div className="cdni__state-nowversion">v{nowVersion}</div>
            </div>
          </div>
        </>
      ) : (
        <div className="cdni__checking">
          <LoadingIndicator />
          <h1>{isLoading ? content?.loading : content?.checkingForUpdates}</h1>
        </div>
      )}
      {ShowUsingDevTip && <div className="cdni__state-dev-tip">{content?.usingDev}</div>}
      <ErrorModal error={error} />
      {content?.enableSoftPromptNotifications && <NotificationsPrompt content={content} />}
      <VideoPlayer
        src={getPath('video_cdni')}
        muted
        autoPlay
        loop
        cover
        volume={1}
        controls={false}
        onPlay={(event, looped) => (looped ? dlogVideoLooped() : dlogVideoPlayed())}
        onWaitingFirstTime={dlogVideoStuttering}
        onFinishedFirstPlaythrough={(event, stutterCount) =>
          dlog({
            phase: 'Video_Player_Active',
            action: `Video_Stuttered:${stutterCount}` as LoginFunnelAction,
            targets: 'CDNI',
          })
        }
      />
      <div className="bg__overlay" />
      <Debug
        enabled={!!content?.showDeveloperOverlay}
        vars={{
          GetTotalSizeToDownload,
          GetAverageDownloadSpeed,
          NowVersion,
          diskFreeSize,
          GetDownloadedSizeInBytes,
          buffer,
          isCDNIFinished,
          sizePerFill,
          currentPhase,
          totalPhases,
          CDNICellularDownloadTipClose,
          CDNIUnexpectedError,
          CDNIPromptName,
          CDNIRecoverableErrorMessage,
          CDNIStateString,
          CDNIStateEnum,
          error,
          CDNIGoToAppStore,
        }}
      >
        <button
          onClick={() => {
            closeWebView();
          }}
        >
          Close Webview
        </button>
      </Debug>
    </>
  );
}
